import { defineQuery, type PortableTextBlock } from "next-sanity"
/**
 * From https://www.sanity.io/docs/presenting-images#6f93276ab029
 * https://www.sanity.io/docs/image-type#df3f768ce073
 */
const imageFields = /* groq */ `
  ...,
  "asset": asset-> {
    altText,
    title,
    description,
    url,
    path,
    assetId,
    extension,
    "tags": opt.media.tags[]->name.current,
    metadata {
      lqip,
      dimensions {
        width,
        height
      }
    }
  }
`

const postFields = /* groq */ `
  _id,
  _type,
  "status": select(_originalId in path("drafts.**") => "draft", "published"),
  title,
  "slug": slug.current,
  ingress,
  excerpt,
  coverMedia {
    mediaType,
    image {${imageFields}},
    video {
      alt,
      caption,
      autoplay,
      loop,
      muted,
      url,
      poster {${imageFields}}
    }
  },
  "coverImage": coalesce(
    select(coverMedia.mediaType == "image" => coverMedia.image{${imageFields}}),
    select(coverMedia.mediaType == "video" && defined(coverMedia.video.poster) => coverMedia.video.poster{${imageFields}}),
    coverImage{${imageFields}}
  ),
  "date": coalesce(date, _updatedAt),
  "author": author->{"name": coalesce(name, "Anonymous"), picture},
  relatedPosts[]{
    _key, // required for drag and drop
    ...@->{
      _id, 
      title, 
      "slug": slug.current,
      excerpt,
      "coverImage": coalesce(
        select(coverMedia.mediaType == "image" => coverMedia.image{${imageFields}}),
        select(coverMedia.mediaType == "video" && defined(coverMedia.video.poster) => coverMedia.video.poster{${imageFields}}),
        coverImage{${imageFields}}
      ),
      date
    } // get fields from the referenced post
  }
`

// single post
export const postQuery = defineQuery(/* groq */ `
  *[_type == "post" && slug.current == $slug][0] {
    content[] {
      ...,
      markDefs[] {
        ...,
        _type == "internalLink" => {
          ...,
          "slug": @.reference->slug.current
        }
      }
    },
    ${postFields}
  }
`)

// list of posts (limit-based pagination: first page limit=6, load more => limit=12, 18, …)
export const postsQuery = defineQuery(/* groq */ `
  *[
    _type == "post" &&
    defined(slug.current) &&
    coalesce(locale, "en") == $locale &&
    !("press-releases" in category[]->slug.current)
  ] | order(date desc, _updatedAt desc) [0...$limit] {
    ${postFields}
  }
`)
export const postsCountQuery = defineQuery(/* groq */ `
  count(*[
    _type == "post" &&
    defined(slug.current) &&
    coalesce(locale, "en") == $locale &&
    !("press-releases" in category[]->slug.current)
  ])
`)

// single press release (in any locale)

export const pressReleaseQuery = defineQuery(/* groq */ `
  *[
    _type == "post" && 
    slug.current == $slug && 
    ("press-releases" in category[]->slug.current)
  ] [0] {
    content,
    ${postFields}
  }
`)

// list of press releases — in any locale
export const pressReleasesQuery = defineQuery(/* groq */ `
  *[
    _type == "post" && 
    defined(slug.current) && 
    ("press-releases" in category[]->slug.current)
  ] | order(date desc, _updatedAt desc) [$start...$end] {
    _id,
    title,
    date,
    "slug": slug.current,
    ingress
  }
`)

export const pressReleasesCountQuery = defineQuery(/* groq */ `
  count(*[
    _type == "post" && 
    defined(slug.current) &&
    ("press-releases" in category[]->slug.current)
  ])
`)

export const pressReleaseBySlugQuery = defineQuery(/* groq */ `
  *[_type == "post" && slug.current == $slug && ("press-releases" in category[]->slug.current)][0] {
    _id,
    title,
    date,
    "slug": slug.current,
    ingress,
    content
  }
`)

// Base fields that all sections share
const sectionFields = /* groq */ `
  _type,
  _key,
  "sectionId": coalesce(sectionId, ""),
  "navigationLabel": coalesce(navigationLabel, ""),
  "showInSectionNavigation": coalesce(showInSectionNavigation, true),
  "visible": coalesce(visible, true),
  "audience": { "showOnlyIn": coalesce(audience.showOnlyIn, []), "hideIn": coalesce(audience.hideIn, []) },
`

// Section-specific fragments
const heroSectionFields = /* groq */ `
  ${sectionFields}
  ...,
  appStoreImage {
    ${imageFields}
  },
  playStoreImage {
    ${imageFields}
  }
`

const heroJourneyStepFields = /* groq */ `
  device,
  label,
  caption,
  message,
  "image": image.asset->url,
  "imageAlt": coalesce(image.alt, ""),
  heading,
  body,
  stat,
  statLabel,
  "attributes": coalesce(attributes[]{ name, value }, []),
  "items": coalesce(items[]{
    title,
    subtitle,
    value,
    "image": image.asset->url,
    "highlighted": coalesce(highlighted, false)
  }, [])
`

// sanity-typegen statically parses this file's GROQ template literals — it
// can't evaluate JS helper functions/array maps interpolated into them, so
// every field name below is written out literally rather than generated.
const heroJourneySectionFields = /* groq */ `
  ${sectionFields}
  margins,
  "stepSeconds": coalesce(stepSeconds, 3),
  "reduceMotion": coalesce(reduceMotion, false),
  "desktop": {
    "heading": coalesce(desktopHeading, ""),
    "body": coalesce(desktopBody, ""),
    "primaryButtonLabel": coalesce(desktopPrimaryButtonLabel, ""),
    "primaryButtonLink": desktopPrimaryButtonLink,
    "secondaryButtonLabel": coalesce(desktopSecondaryButtonLabel, ""),
    "secondaryButtonLink": desktopSecondaryButtonLink,
    "pillOnline": coalesce(desktopPillOnline, ""),
    "pillInstore": coalesce(desktopPillInstore, ""),
    "onlineJourney": {
      "donated": desktopOnlineSteps[step == "donated"][0] { ${heroJourneyStepFields} },
      "scanned": desktopOnlineSteps[step == "scanned"][0] { ${heroJourneyStepFields} },
      "listed": desktopOnlineSteps[step == "listed"][0] { ${heroJourneyStepFields} },
      "priced": desktopOnlineSteps[step == "priced"][0] { ${heroJourneyStepFields} },
      "sized": desktopOnlineSteps[step == "sized"][0] { ${heroJourneyStepFields} },
      "styled": desktopOnlineSteps[step == "styled"][0] { ${heroJourneyStepFields} },
      "published": desktopOnlineSteps[step == "published"][0] { ${heroJourneyStepFields} },
      "sold": desktopOnlineSteps[step == "sold"][0] { ${heroJourneyStepFields} },
      "reporting": desktopOnlineSteps[step == "reporting"][0] { ${heroJourneyStepFields} }
    },
    "inStoreJourney": {
      "donated": desktopInstoreSteps[step == "donated"][0] { ${heroJourneyStepFields} },
      "scanned": desktopInstoreSteps[step == "scanned"][0] { ${heroJourneyStepFields} },
      "priced": desktopInstoreSteps[step == "priced"][0] { ${heroJourneyStepFields} },
      "tagged": desktopInstoreSteps[step == "tagged"][0] { ${heroJourneyStepFields} },
      "window": desktopInstoreSteps[step == "window"][0] { ${heroJourneyStepFields} },
      "sold": desktopInstoreSteps[step == "sold"][0] { ${heroJourneyStepFields} },
      "reporting": desktopInstoreSteps[step == "reporting"][0] { ${heroJourneyStepFields} }
    }
  },
  "mobile": {
    "heading": coalesce(mobileHeading, ""),
    "body": coalesce(mobileBody, ""),
    "primaryButtonLabel": coalesce(mobilePrimaryButtonLabel, ""),
    "primaryButtonLink": mobilePrimaryButtonLink,
    "secondaryButtonLabel": coalesce(mobileSecondaryButtonLabel, ""),
    "secondaryButtonLink": mobileSecondaryButtonLink,
    "pillOnline": coalesce(mobilePillOnline, ""),
    "pillInstore": coalesce(mobilePillInstore, ""),
    "onlineJourney": {
      "donated": mobileOnlineSteps[step == "donated"][0] { ${heroJourneyStepFields} },
      "scanned": mobileOnlineSteps[step == "scanned"][0] { ${heroJourneyStepFields} },
      "listed": mobileOnlineSteps[step == "listed"][0] { ${heroJourneyStepFields} },
      "priced": mobileOnlineSteps[step == "priced"][0] { ${heroJourneyStepFields} },
      "sized": mobileOnlineSteps[step == "sized"][0] { ${heroJourneyStepFields} },
      "styled": mobileOnlineSteps[step == "styled"][0] { ${heroJourneyStepFields} },
      "published": mobileOnlineSteps[step == "published"][0] { ${heroJourneyStepFields} },
      "sold": mobileOnlineSteps[step == "sold"][0] { ${heroJourneyStepFields} },
      "reporting": mobileOnlineSteps[step == "reporting"][0] { ${heroJourneyStepFields} }
    },
    "inStoreJourney": {
      "donated": mobileInstoreSteps[step == "donated"][0] { ${heroJourneyStepFields} },
      "scanned": mobileInstoreSteps[step == "scanned"][0] { ${heroJourneyStepFields} },
      "priced": mobileInstoreSteps[step == "priced"][0] { ${heroJourneyStepFields} },
      "tagged": mobileInstoreSteps[step == "tagged"][0] { ${heroJourneyStepFields} },
      "window": mobileInstoreSteps[step == "window"][0] { ${heroJourneyStepFields} },
      "sold": mobileInstoreSteps[step == "sold"][0] { ${heroJourneyStepFields} },
      "reporting": mobileInstoreSteps[step == "reporting"][0] { ${heroJourneyStepFields} }
    }
  }
`

const heroVideoSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const heroContentSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const contentSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const trustedByCompanyFields = /* groq */ `
  _type,
  _key,
  "name": coalesce(name, ""),
  logoOrVideo {
    mediaType,
    image {${imageFields}},
    video {
      alt,
      caption,
      autoplay,
      loop,
      muted,
      url,
      poster {${imageFields}}
    }
  },
  "width": coalesce(width, ""),
  "sizeConstraint": coalesce(sizeConstraint, "auto"),
  "standardizeLogo": coalesce(standardizeLogo, true)
`

const trustedBySectionFields = /* groq */ `
  ${sectionFields}
  "heading": coalesce(heading, ""),
  "logoSource": coalesce(logoSource, "custom"),
  "partnerLogoSet": partnerLogoSet->{
    _id,
    title,
    partners[]{
      ${trustedByCompanyFields}
    }
  },
  companies[]{
    ${trustedByCompanyFields}
  },
  animationSettings {
    "speed": coalesce(speed, "normal"),
    "pauseOnHover": coalesce(pauseOnHover, true),
    "pauseOnScroll": coalesce(pauseOnScroll, true),
    "showFadeEdges": coalesce(showFadeEdges, true),
    "logoSpacing": coalesce(logoSpacing, "normal")
  },
  margins,
  "backgroundColor": coalesce(backgroundColor, ""),
  "customBackgroundColor": coalesce(customBackgroundColor, "")
`

const featuresSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const featureCardsSectionFields = /* groq */ `
  ${sectionFields}
  heading,
  cards[]{
    _key,
    categoryLabel,
    icon,
    stat,
    description,
  },
`

const aboutSectionFields = /* groq */ `
  ${sectionFields}
  "label": coalesce(label, ""),
  "heading": coalesce(heading, ""),
  "description": coalesce(description, ""),
  heroImage{
    ${imageFields}
  },
  metrics[]{
    _type,
    _key,
    "value": coalesce(value, ""),
    "label": coalesce(label, ""),
    "accentColor": coalesce(accentColor, "accent-purple")
  },
  panels[]{
    _type,
    _key,
    "title": coalesce(title, ""),
    "content": coalesce(content, ""),
    "bulletPoints": bulletPoints[]
  },
  margins
`

const comparePricingTableSectionFields = /* groq */ `
  ${sectionFields}
  ...,
  "heading": coalesce(heading, ""),
  "featuresRowLabel": coalesce(featuresRowLabel, "Features"),
  plans[]{
    _key,
    "name": coalesce(name, ""),
    "buttonText": coalesce(buttonText, ""),
    buttonHref,
    "highlighted": coalesce(highlighted, false)
  },
  featureRows[]{
    _key,
    "featureLabel": coalesce(featureLabel, ""),
    planValues[]
  }
`

// Plan Pricing (MIN-3953). Note what ISN'T here: no plan names, prices,
// features or comparison rows. Those come from `@minimist/pricing` and the
// billing service's live curve, so the page can't advertise a package the
// product doesn't sell. `margins` is listed explicitly because `sectionFields`
// doesn't carry it and this projection doesn't spread `...`.
const planPricingSectionFields = /* groq */ `
  ${sectionFields}
  margins,
  "eyebrow": coalesce(eyebrow, ""),
  "heading": coalesce(heading, ""),
  "intro": coalesce(intro, ""),
  "defaultVolume": coalesce(defaultVolume, 700),
  "defaultCurrency": coalesce(defaultCurrency, "locale"),
  "defaultInterval": coalesce(defaultInterval, "month"),
  "highlightTier": coalesce(highlightTier, "none"),
  "highlightLabel": coalesce(highlightLabel, ""),
  "showComparison": coalesce(showComparison, true),
  "comparisonDefaultOpen": coalesce(comparisonDefaultOpen, false),
  "showEnterprise": coalesce(showEnterprise, true),
  "ctaText": coalesce(ctaText, "Start free trial"),
  ctaHref,
  "ctaPageSlug": ctaPage->slug.current,
  "enterpriseCtaText": coalesce(enterpriseCtaText, "Talk to us"),
  enterpriseCtaHref,
  "footnote": coalesce(footnote, ""),
  "backgroundColor": coalesce(backgroundColor, ""),
  "backgroundColorCustom": coalesce(backgroundColorCustom, "")
`

const calculatorSectionFields = /* groq */ `
      ${sectionFields}
      "eyebrow": coalesce(eyebrow, ""),
      "heading": coalesce(heading, ""),
      "description": coalesce(description, ""),
      preambleImage {
        ${imageFields}
      },
      preambleImageMobile {
        ${imageFields}
      },
      tabs[]{
        _type,
        _key,
        "tabTitle": coalesce(tabTitle, ""),
        _type == "roiCalculatorTab" => {
          roiConfig{
            "storesLabel": coalesce(storesLabel[_key == $locale][0].value, storesLabel[_key == "en"][0].value, "Number of stores"),
            "itemsPerStoreLabel": coalesce(itemsPerStoreLabel[_key == $locale][0].value, itemsPerStoreLabel[_key == "en"][0].value, "Items listed per store each week"),
            "projectedImpactLabel": coalesce(projectedImpactLabel[_key == $locale][0].value, projectedImpactLabel[_key == "en"][0].value, "Projected impact"),
            "itemsPerWeekLabel": coalesce(itemsPerWeekLabel[_key == $locale][0].value, itemsPerWeekLabel[_key == "en"][0].value, "items / week"),
            "defaultStores": coalesce(defaultStores, 1),
            "minStores": coalesce(minStores, 1),
            "defaultItemsPerStore": coalesce(defaultItemsPerStore, 1),
            "minItemsPerStore": coalesce(minItemsPerStore, 1),
            "averageSalePrice": coalesce(averageSalePrice, 0),
            "averageSalePriceLabel": coalesce(averageSalePriceLabel, ""),
            "averageSalePriceDisclaimer": coalesce(averageSalePriceDisclaimer, ""),
            "revenueLiftInStorePercent": coalesce(revenueLiftInStorePercent, 0),
            "revenueLiftInStorePercentLabel": coalesce(revenueLiftInStorePercentLabel, ""),
            "revenueLiftInStoreDisclaimer": coalesce(revenueLiftInStoreDisclaimer, ""),
            "revenueLiftEcommercePercent": coalesce(revenueLiftEcommercePercent, 0),
            "revenueLiftEcommercePercentLabel": coalesce(revenueLiftEcommercePercentLabel, ""),
            "revenueLiftEcommerceDisclaimer": coalesce(revenueLiftEcommerceDisclaimer, ""),
            "timeSavedPerItemMinutes": coalesce(timeSavedPerItemMinutes, 0),
            "timeSavedPerItemMinutesLabel": coalesce(timeSavedPerItemMinutesLabel, ""),
            "timeSavedPerItemDisclaimer": coalesce(timeSavedPerItemDisclaimer, ""),
            "hourlyLabourCost": coalesce(hourlyLabourCost, 0),
            "hourlyLabourCostLabel": coalesce(hourlyLabourCostLabel, ""),
            "hourlyLabourCostDisclaimer": coalesce(hourlyLabourCostDisclaimer, ""),
            "showPlatformCost": coalesce(showPlatformCost, true),
            "platformCostPerMonth": coalesce(platformCostPerMonth, 0),
            "platformCostPerMonthLabel": coalesce(platformCostPerMonthLabel, ""),
            "platformCostDisclaimer": coalesce(platformCostDisclaimer, ""),
            "currency": coalesce(currency, "£"),
            "currencyLabel": coalesce(currencyLabel, ""),
            "currencyDisclaimer": coalesce(currencyDisclaimer, ""),
            "storesDisclaimer": coalesce(storesDisclaimer, ""),
            "itemsPerStoreDisclaimer": coalesce(itemsPerStoreDisclaimer, ""),
            "projectedImpactDescription": coalesce(projectedImpactDescription[_key == $locale][0].value, projectedImpactDescription[_key == "en"][0].value, "Based on your inputs and Sanity assumptions."),
            "weeklyRevenueUpliftLabel": coalesce(weeklyRevenueUpliftLabel[_key == $locale][0].value, weeklyRevenueUpliftLabel[_key == "en"][0].value, "Weekly revenue uplift"),
            "weeklyRevenueUpliftDescription": coalesce(weeklyRevenueUpliftDescription[_key == $locale][0].value, weeklyRevenueUpliftDescription[_key == "en"][0].value, "From improved pricing & listing quality."),
            "weeklyLabourSavingsLabel": coalesce(weeklyLabourSavingsLabel[_key == $locale][0].value, weeklyLabourSavingsLabel[_key == "en"][0].value, "Weekly labour savings"),
            "hoursSavedLabel": coalesce(hoursSavedLabel[_key == $locale][0].value, hoursSavedLabel[_key == "en"][0].value, "hours saved"),
            "annualBenefitLabel": coalesce(annualBenefitLabel[_key == $locale][0].value, annualBenefitLabel[_key == "en"][0].value, "Annual benefit"),
            "annualBenefitDescription": coalesce(annualBenefitDescription[_key == $locale][0].value, annualBenefitDescription[_key == "en"][0].value, "Total uplift from sales + saved time."),
            "monthlyRoiLabel": coalesce(monthlyRoiLabel[_key == $locale][0].value, monthlyRoiLabel[_key == "en"][0].value, "Monthly ROI"),
            "breakEvenLabel": coalesce(breakEvenLabel[_key == $locale][0].value, breakEvenLabel[_key == "en"][0].value, "Break-even in ~"),
            "weeksLabel": coalesce(weeksLabel[_key == $locale][0].value, weeksLabel[_key == "en"][0].value, " weeks"),
            "roiNotAvailableLabel": coalesce(roiNotAvailableLabel[_key == $locale][0].value, roiNotAvailableLabel[_key == "en"][0].value, "Set a platform cost to see ROI"),
            "totalWeeklyBenefitLabel": coalesce(totalWeeklyBenefitLabel[_key == $locale][0].value, totalWeeklyBenefitLabel[_key == "en"][0].value, "Total weekly benefit"),
            "revenueUpliftLabel": coalesce(revenueUpliftLabel[_key == $locale][0].value, revenueUpliftLabel[_key == "en"][0].value, "Revenue uplift"),
            "labourSavingsLabel": coalesce(labourSavingsLabel[_key == $locale][0].value, labourSavingsLabel[_key == "en"][0].value, "Labour savings"),
            "perMonthLabel": coalesce(perMonthLabel[_key == $locale][0].value, perMonthLabel[_key == "en"][0].value, "per month"),
            accent, highlightBg, highlightText, highlightLabel, cardBg, cardBorder, sectionTitle, labelColor, valueColor, tabContentBg
          }
        },
        _type == "timeSavingsCalculatorTab" => {
          timeSavingsConfig{
            "defaultListingsPerWeek": coalesce(defaultListingsPerWeek, 10),
            "listingsPerWeekLabel": coalesce(listingsPerWeekLabel[_key == $locale][0].value, listingsPerWeekLabel[_key == "en"][0].value, "Listings per week"),
            "listingsPerWeekDisclaimer": coalesce(listingsPerWeekDisclaimer, ""),
            "minListingsPerWeek": coalesce(minListingsPerWeek, 1),
            "minutesPerListingLabel": coalesce(minutesPerListingLabel[_key == $locale][0].value, minutesPerListingLabel[_key == "en"][0].value, "Average minutes per listing"),
            "minutesPerListingDisclaimer": coalesce(minutesPerListingDisclaimer, ""),
            "defaultMinutesPerListing": coalesce(defaultMinutesPerListing, 15),
            "minMinutesPerListing": coalesce(minMinutesPerListing, 1),
            "hoursPerDay": coalesce(hoursPerDay, 8),
            "hoursPerDayLabel": coalesce(hoursPerDayLabel, ""),
            "hoursPerDayDisclaimer": coalesce(hoursPerDayDisclaimer, ""),
            "hourlyLabourCost": coalesce(hourlyLabourCost, 20),
            "hourlyLabourCostLabel": coalesce(hourlyLabourCostLabel, ""),
            "hourlyLabourCostDisclaimer": coalesce(hourlyLabourCostDisclaimer, ""),
            "currency": coalesce(currency, "£"),
            "currencyLabel": coalesce(currencyLabel, ""),
            "currencyDisclaimer": coalesce(currencyDisclaimer, ""),
            "baselineSecondsPerListing": coalesce(baselineSecondsPerListing, 30),
            "minutesPerListingWithMinimist": coalesce(minutesPerListingWithMinimist, 0.5),
            "minutesPerListingWithMinimistLabel": coalesce(minutesPerListingWithMinimistLabel, ""),
            "minutesPerListingWithMinimistDisclaimer": coalesce(minutesPerListingWithMinimistDisclaimer, ""),
            "percentItemsNeedingPricingHelp": coalesce(percentItemsNeedingPricingHelp, 70),
            "percentItemsNeedingPricingHelpLabel": coalesce(percentItemsNeedingPricingHelpLabel, ""),
            "percentItemsNeedingPricingHelpDisclaimer": coalesce(percentItemsNeedingPricingHelpDisclaimer, ""),
            "pricingMinutesSavedPerItemLabel": coalesce(pricingMinutesSavedPerItemLabel, ""),
            "projectedImpactLabel": coalesce(projectedImpactLabel[_key == $locale][0].value, projectedImpactLabel[_key == "en"][0].value, "Save"),
            "projectedImpactDescription": coalesce(projectedImpactDescription[_key == $locale][0].value, projectedImpactDescription[_key == "en"][0].value, "Based on your inputs and assumptions."),
            accent, highlightBg, highlightText, highlightLabel, cardBg, cardBorder, sectionTitle, labelColor, valueColor, tabContentBg
          }
        },
        _type == "pricingCalculatorTab" => {
          "outcomesFirst": coalesce(outcomesFirst, false),
          "segmentOrder": segmentOrder[]{"segmentType": segmentType},
          pricingConfig{
            "defaultShopSalesPa": coalesce(defaultShopSalesPa, 120000),
            "defaultTotalUnitsPa": coalesce(defaultTotalUnitsPa, 34632),
            "defaultInStoreAtv": coalesce(defaultInStoreAtv, 7.7),
            "defaultTimeSavedPerCheck": coalesce(defaultTimeSavedPerCheck, 3.5),
            "defaultAiPriceChecksPercent": coalesce(defaultAiPriceChecksPercent, 30),
            "defaultAtvLiftPercent": coalesce(defaultAtvLiftPercent, 4.5),
            "defaultHourlyLabourCost": coalesce(defaultHourlyLabourCost, 13),
            "defaultCostPerUse": coalesce(defaultCostPerUse, 0.18),
            "currency": coalesce(currency, "£"),
            "featureExplainer": coalesce(featureExplainer[_key == $locale][0].value, featureExplainer[_key == "en"][0].value, ""),
            // Subheading labels (localised)
            "shopSalesLabel": coalesce(shopSalesLabel[_key == $locale][0].value, shopSalesLabel[_key == "en"][0].value),
            "totalUnitsLabel": coalesce(totalUnitsLabel[_key == $locale][0].value, totalUnitsLabel[_key == "en"][0].value),
            "inStoreAtvLabel": coalesce(inStoreAtvLabel[_key == $locale][0].value, inStoreAtvLabel[_key == "en"][0].value),
            "timeSavedPerCheckLabel": coalesce(timeSavedPerCheckLabel[_key == $locale][0].value, timeSavedPerCheckLabel[_key == "en"][0].value),
            "aiPriceChecksLabel": coalesce(aiPriceChecksLabel[_key == $locale][0].value, aiPriceChecksLabel[_key == "en"][0].value),
            "atvLiftLabel": coalesce(atvLiftLabel[_key == $locale][0].value, atvLiftLabel[_key == "en"][0].value),
            "staffHoursSavedLabel": coalesce(staffHoursSavedLabel[_key == $locale][0].value, staffHoursSavedLabel[_key == "en"][0].value),
            "staffResourceSavingsLabel": coalesce(staffResourceSavingsLabel[_key == $locale][0].value, staffResourceSavingsLabel[_key == "en"][0].value),
            "totalSalesLiftLabel": coalesce(totalSalesLiftLabel[_key == $locale][0].value, totalSalesLiftLabel[_key == "en"][0].value),
            "netBenefitLabel": coalesce(netBenefitLabel[_key == $locale][0].value, netBenefitLabel[_key == "en"][0].value),
            "costLabel": coalesce(costLabel[_key == $locale][0].value, costLabel[_key == "en"][0].value),
            "projectedRoiLabel": coalesce(projectedRoiLabel[_key == $locale][0].value, projectedRoiLabel[_key == "en"][0].value),
            // Section titles
            "storeExampleTitle": coalesce(storeExampleTitle, ""),
            "keyAssumptionsTitle": coalesce(keyAssumptionsTitle, ""),
            "projectedImpactTitle": coalesce(projectedImpactTitle, ""),
            "costRoiTitle": coalesce(costRoiTitle, ""),
            accent, highlightBg, highlightText, highlightLabel, cardBg, cardBorder, sectionTitle, labelColor, valueColor, tabContentBg
          }
        },
        _type == "listingAutomationCalculatorTab" => {
          "outcomesFirst": coalesce(outcomesFirst, false),
          "segmentOrder": segmentOrder[]{"segmentType": segmentType},
          listingAutomationConfig{
            "defaultBaselineListings": coalesce(defaultBaselineListings, 20),
            "defaultTargetListings": coalesce(defaultTargetListings, 200),
            "defaultAvgOnlineAtv": coalesce(defaultAvgOnlineAtv, 40),
            "defaultTimeSavedPerListing": coalesce(defaultTimeSavedPerListing, 4.5),
            "defaultConversionStandard": coalesce(defaultConversionStandard, 5),
            "defaultConversionWithAi": coalesce(defaultConversionWithAi, 7.5),
            "defaultCostPerUse": coalesce(defaultCostPerUse, 0.5),
            "currency": coalesce(currency, "£"),
            "featureExplainer": coalesce(featureExplainer[_key == $locale][0].value, featureExplainer[_key == "en"][0].value, ""),
            // Section titles
            "storeExampleTitle": coalesce(storeExampleTitle, ""),
            "keyAssumptionsTitle": coalesce(keyAssumptionsTitle, ""),
            "projectedImpactTitle": coalesce(projectedImpactTitle, ""),
            "costRoiTitle": coalesce(costRoiTitle, ""),
            // Subheading labels (localised)
            "baselineListingsLabel": coalesce(baselineListingsLabel[_key == $locale][0].value, baselineListingsLabel[_key == "en"][0].value),
            "targetListingsLabel": coalesce(targetListingsLabel[_key == $locale][0].value, targetListingsLabel[_key == "en"][0].value),
            "avgOnlineAtvLabel": coalesce(avgOnlineAtvLabel[_key == $locale][0].value, avgOnlineAtvLabel[_key == "en"][0].value),
            "timeSavedPerListingLabel": coalesce(timeSavedPerListingLabel[_key == $locale][0].value, timeSavedPerListingLabel[_key == "en"][0].value),
            "conversionStandardLabel": coalesce(conversionStandardLabel[_key == $locale][0].value, conversionStandardLabel[_key == "en"][0].value),
            "conversionWithAiLabel": coalesce(conversionWithAiLabel[_key == $locale][0].value, conversionWithAiLabel[_key == "en"][0].value),
            "staffHoursSavedLabel": coalesce(staffHoursSavedLabel[_key == $locale][0].value, staffHoursSavedLabel[_key == "en"][0].value),
            "additionalListingsLabel": coalesce(additionalListingsLabel[_key == $locale][0].value, additionalListingsLabel[_key == "en"][0].value),
            "additionalSalesLabel": coalesce(additionalSalesLabel[_key == $locale][0].value, additionalSalesLabel[_key == "en"][0].value),
            "netBenefitLabel": coalesce(netBenefitLabel[_key == $locale][0].value, netBenefitLabel[_key == "en"][0].value),
            "costLabel": coalesce(costLabel[_key == $locale][0].value, costLabel[_key == "en"][0].value),
            "projectedRoiLabel": coalesce(projectedRoiLabel[_key == $locale][0].value, projectedRoiLabel[_key == "en"][0].value),
            accent, highlightBg, highlightText, highlightLabel, cardBg, cardBorder, sectionTitle, labelColor, valueColor, tabContentBg
          }
        },
        _type == "aiModelsCalculatorTab" => {
          "outcomesFirst": coalesce(outcomesFirst, false),
          "segmentOrder": segmentOrder[]{"segmentType": segmentType},
          aiModelsConfig{
            "defaultTargetListings": coalesce(defaultTargetListings, 200),
            "defaultModelUsesPercent": coalesce(defaultModelUsesPercent, 50),
            "defaultAvgOnlineAtv": coalesce(defaultAvgOnlineAtv, 40),
            "defaultBaselineConversion": coalesce(defaultBaselineConversion, 7.5),
            "defaultConversionLift": coalesce(defaultConversionLift, 40),
            "defaultCostPerUse": coalesce(defaultCostPerUse, 0.39),
            "currency": coalesce(currency, "£"),
            "featureExplainer": coalesce(featureExplainer[_key == $locale][0].value, featureExplainer[_key == "en"][0].value, ""),
            // Section titles
            "storeExampleTitle": coalesce(storeExampleTitle, ""),
            "keyAssumptionsTitle": coalesce(keyAssumptionsTitle, ""),
            "projectedImpactTitle": coalesce(projectedImpactTitle, ""),
            "costRoiTitle": coalesce(costRoiTitle, ""),
            // Subheading labels (localised)
            "targetListingsLabel": coalesce(targetListingsLabel[_key == $locale][0].value, targetListingsLabel[_key == "en"][0].value),
            "modelUsesLabel": coalesce(modelUsesLabel[_key == $locale][0].value, modelUsesLabel[_key == "en"][0].value),
            "avgOnlineAtvLabel": coalesce(avgOnlineAtvLabel[_key == $locale][0].value, avgOnlineAtvLabel[_key == "en"][0].value),
            "baselineConversionLabel": coalesce(baselineConversionLabel[_key == $locale][0].value, baselineConversionLabel[_key == "en"][0].value),
            "conversionLiftLabel": coalesce(conversionLiftLabel[_key == $locale][0].value, conversionLiftLabel[_key == "en"][0].value),
            "newConversionRateLabel": coalesce(newConversionRateLabel[_key == $locale][0].value, newConversionRateLabel[_key == "en"][0].value),
            "additionalSalesLabel": coalesce(additionalSalesLabel[_key == $locale][0].value, additionalSalesLabel[_key == "en"][0].value),
            "additionalSalesRevenueLabel": coalesce(additionalSalesRevenueLabel[_key == $locale][0].value, additionalSalesRevenueLabel[_key == "en"][0].value),
            "netReturnPerModelLabel": coalesce(netReturnPerModelLabel[_key == $locale][0].value, netReturnPerModelLabel[_key == "en"][0].value),
            "netBenefitLabel": coalesce(netBenefitLabel[_key == $locale][0].value, netBenefitLabel[_key == "en"][0].value),
            "costLabel": coalesce(costLabel[_key == $locale][0].value, costLabel[_key == "en"][0].value),
            "projectedRoiLabel": coalesce(projectedRoiLabel[_key == $locale][0].value, projectedRoiLabel[_key == "en"][0].value),
            accent, highlightBg, highlightText, highlightLabel, cardBg, cardBorder, sectionTitle, labelColor, valueColor, tabContentBg
          }
        }
      },
      "assumptions": assumptions[],
      "assumptionsDisclaimer": select(
        defined(assumptionsDisclaimer[_key == $locale][0]) => assumptionsDisclaimer[_key == $locale][0].value,
        defined(assumptionsDisclaimer[_key == "en"][0]) => assumptionsDisclaimer[_key == "en"][0].value,
        []
      ),
      "footnote": coalesce(footnote, ""),
      formCode{
        language,
        code,
        filename
      },
      "ctaHeading": coalesce(ctaHeading, ""),
      ctaButton{
        "label": coalesce(label, ""),
        link[]{
          _key,
          "label": coalesce(label[_key == $locale][0].value, label[_key == "en"][0].value),
          type,
          internalLink->{
            _type == "category" => {
              _type,
              _id,
              _ref,
              locale,
              "slug": slug.current,
              parent->{
                _type,
                _id,
                _ref,
                locale,
                "slug": slug.current,
              }
            },
            _type == "page" => {
              _type,
              _id,
              _ref,
              locale,
              "slug": slug.current,
              parent->{
                _type,
                _id,
                _ref,
                locale,
                "slug": slug.current,
              }
            },
            _type == "post" => {
              _type,
              _id,
              locale,
              "slug": slug.current,
            }
          },
          externalUrl,
          eventName,
          "isVisible": coalesce(isVisible[_key == $locale][0].value, isVisible[_key == "en"][0].value, true)
        }
      },
      margins
    `

const ctaSectionFields = /* groq */ `
  ${sectionFields}
  ...,
  appStoreImage {
    ${imageFields}
  },
  playStoreImage {
    ${imageFields}
  },
  tallyFormId,
  "tallyFormHeight": coalesce(tallyFormHeight, 400),
  "tallyTransparentBackground": coalesce(tallyTransparentBackground, false),
  "backgroundColor": coalesce(backgroundColor, background),
  "backgroundColorCustom": coalesce(backgroundColorCustom, customBackground),
  button { label, action, link },
  "buttonPlacement": coalesce(buttonPlacement, "below")
`

const ctaBandSectionFields = /* groq */ `
  ${sectionFields}
  heading,
  subcopy,
  buttonLabel,
  showAppBadges,
`

const phoneDemoVideoSectionFields = /* groq */ `
  ${sectionFields}
  videoId,
  eyebrow,
  heading,
  subcopy,
  "poster": poster.asset->url,
`

const ctaButtonSectionFields = /* groq */ `
  ${sectionFields}
  label,
  href,
  margins
`

const customerQuoteSectionFields = /* groq */ `
  ${sectionFields}
  testimonials[]{
    _type,
    _key,
    quote,
    attribution { name, title, company },
    companyLogo { image, altText },
    images[]{ _key, image, altText },
    ctaButton {
      "action": coalesce(action, "link"),
      text,
      link,
      "openInNewTab": coalesce(openInNewTab, false)
    }
  },
  ctaButton {
    "action": coalesce(action, "link"),
    text,
    link,
    "openInNewTab": coalesce(openInNewTab, false)
  },
  "showNavigationArrows": coalesce(showNavigationArrows, true),
  "autoplayIntervalSeconds": coalesce(autoplayIntervalSeconds, 5),
  "backgroundColor": coalesce(backgroundColor, ""),
  "backgroundColorCustom": coalesce(backgroundColorCustom, "")
`

const faqSectionFields = /* groq */ `
  ${sectionFields}
  sectionTagLabel,
  heading,
  subheading,
  expandAll,
  backgroundColor,
  customBackgroundColor,
  itemColor,
  itemColorCustom,
  faqs[]{
    _type,
    _key,
    question,
    answer
  }
`

const itemTypesSectionFields = /* groq */ `
  ${sectionFields}
  sectionTagLabel,
  heading,
  subheading,
  categories[]{
    _type,
    _key,
    title,
    description,
    icon { ${imageFields} },
    showcaseImages[]{
      _type,
      _key,
      ${imageFields},
      caption
    },
    isDefaultOpen
  }
`

const statsSectionFields = /* groq */ `
  ${sectionFields}
  ...,
  "backgroundColor": coalesce(backgroundColor, ""),
  "backgroundColorCustom": coalesce(backgroundColorCustom, "")
`

const gallerySectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const twoColumnSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const downloadGateSectionFields = /* groq */ `
  ${sectionFields}
  "heading": coalesce(heading, ""),
  "description": coalesce(description, ""),
  "emailPlaceholder": coalesce(emailPlaceholder, ""),
  "submitLabel": coalesce(submitLabel, ""),
  "successHeading": coalesce(successHeading, ""),
  "successDescription": coalesce(successDescription, ""),
  "downloadLinkLabel": coalesce(downloadLinkLabel, ""),
  downloadUrl,
  "submissionContext": coalesce(submissionContext, ""),
  margins
`

const mapSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const teamSectionFields = /* groq */ `
  ${sectionFields}
  "heading": coalesce(heading, ""),
  "description": coalesce(description, ""),
  "members": members[]{
    _type,
    _key,
    "name": coalesce(name, ""),
    "role": coalesce(role, ""),
    "bio": coalesce(bio, ""),
    linkedinUrl,
    email,
    "photo": photo{
      ${imageFields}
    }
  },
  margins
`

const pressSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

// Logos resolve to a bare CDN url rather than an image object: the band renders each
// mark either as a plain image or as a CSS mask filled with the ink colour (the
// monochrome option), and mask-image needs a url. Copy defaults live here so an empty
// CMS field still renders the approved line rather than a blank.
const supportedMarketplacesSectionFields = /* groq */ `
  ${sectionFields}
  "heading": coalesce(heading, "Already selling on these? So are we."),
  "logoTone": coalesce(logoTone, "color"),
  "backgroundColor": coalesce(backgroundColor, "offWhite"),
  "backgroundColorCustom": coalesce(backgroundColorCustom, ""),
  "marketplaces": marketplaces[]{
    _key,
    "name": coalesce(name, ""),
    "logoUrl": logo.asset->url,
    "scale": coalesce(scale, 1)
  },
  margins
`

const textWithIllustrationSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const pageHeaderSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, ""),
  "subtitle": coalesce(subtitle, ""),
  margins {
    type,
    all,
    vertical,
    horizontal,
    top,
    right,
    bottom,
    left
  },
  backgroundColor,
  backgroundColorCustom,
`

const spacerSectionFields = /* groq */ `
  ${sectionFields}
  ...
`

const interactiveHeroSectionFields = /* groq */ `
  ${sectionFields}
  tagLine,
  "heading": coalesce(heading, ""),
  body,
  primaryButton {
    "label": coalesce(label, ""),
    "action": coalesce(action, "contactForm"),
    link
  },
  secondaryButtonLabel,
  appStoreLink,
  ctaSubline,
  stage4Heading,
  stage4SubLine,
  stage4SyncNote,
  stage4DashboardLabel,
  stage4AvgTimeToSell,
  trustSignals,
  backgroundColor,
  backgroundColorCustom,
  "items": items[] {
    _key,
    "label": coalesce(label, ""),
    "photo": photo.asset->url,
    "photoFit": coalesce(photoFit, "cover"),
    "cleanPhoto": cleanPhoto.asset->url,
    "modelPhoto": modelPhoto.asset->url,
    "title": coalesce(title, ""),
    "description": coalesce(description, ""),
    "priceMin": coalesce(priceMin, 0),
    "priceMax": coalesce(priceMax, 0),
    "priceCurrency": coalesce(priceCurrency, "£"),
    "similarSales": coalesce(similarSales, 0),
    "attributes": coalesce(attributes[] {
      _key,
      "name": coalesce(name, ""),
      "value": coalesce(value, "")
    }, []),
    "showSizing": coalesce(showSizing, false),
    "sizing": coalesce(sizing[] {
      _key,
      "label": coalesce(label, ""),
      "value": coalesce(value, "")
    }, []),
    "bulkPhotos": coalesce(bulkPhotos[].asset->url, [photo.asset->url]),
    "marketplaces": coalesce(marketplaces, []),
    "timeToSell": coalesce(timeToSell, "")
  }
`

const contentBlocksSectionFields = /* groq */ `
  ${sectionFields}
  blocks[]{
    _key,
    title,
    body,
    bullets,
    imageFit,
    "image": image.asset->url,
    "images": images[]{ "url": asset->url, "alt": alt }
  }
`

const keyBenefitsSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, ""),
  benefits[]{
    _type,
    _key,
    "text": coalesce(text, ""),
    "description": coalesce(description, "")
  },
  images[]{ ..., "alt": coalesce(alt, "") },
  illustration,
  "backgroundColor": coalesce(backgroundColor, ""),
  "backgroundColorCustom": coalesce(backgroundColorCustom, "")
`

const videoEmbedSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, ""),
  "description": coalesce(description, ""),
  videoUrl,
  posterImage,
  "style": coalesce(style, "embed"),
  eyebrow,
  filmPageLabel,
  "showPlayButton": coalesce(showPlayButton, false),
  "videoSize": coalesce(videoSize, "wide"),
  "aspectRatio": coalesce(aspectRatio, "16:9"),
  ctaButton {
    "action": coalesce(action, "link"),
    "text": coalesce(text, ""),
    link,
    "openInNewTab": coalesce(openInNewTab, false)
  }
`

const imageBlockSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, ""),
  image {
    ${imageFields}
  },
  imageMobile {
    ${imageFields}
  },
  "caption": coalesce(caption, ""),
  "fullWidth": coalesce(fullWidth, false),
  "ctaPosition": coalesce(ctaPosition, "bottomMiddle"),
  ctaButton {
    "action": coalesce(action, "link"),
    "text": coalesce(text, ""),
    link,
    "openInNewTab": coalesce(openInNewTab, false)
  }
`

const contentSliderSectionFields = /* groq */ `
  ${sectionFields}
  "intervalSeconds": coalesce(intervalSeconds, 5),
  "fullWidth": coalesce(fullWidth, true),
  "topGradient": {
    "enabled": coalesce(topGradient.enabled, true),
    "height": coalesce(topGradient.height, "medium"),
    "strength": coalesce(topGradient.strength, "medium")
  },
  "animation": coalesce(animation, "slideLeft"),
  "animationDuration": coalesce(animationDuration, "500"),
  margins,
  slides[]{
    _type == "pageHeaderSection" => {
      ${pageHeaderSectionFields}
    },
    _type == "videoEmbedSection" => {
      ${videoEmbedSectionFields}
    },
    _type == "imageBlockSection" => {
      ...,
      ${imageBlockSectionFields}
    }
  }
`

const whyChooseSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, ""),
  reasons[]{
    _type,
    _key,
    "number": coalesce(number, ""),
    "title": coalesce(title, ""),
    "description": coalesce(description, ""),
    image {
      ${imageFields}
    }
  },
  "backgroundColor": coalesce(backgroundColor, "dark"),
  "customBackgroundColor": coalesce(customBackgroundColor, ""),
  cardColor,
  cardColorCustom
`

const problemsSectionRedesignFeatureFields = /* groq */ `
  cardEyebrow,
  cardProblem,
  cardBody,
  cardCtaLabel,
  cardMetricValue,
  cardMetricLabel,
  featureName,
  category,
  bannerHeadline,
  bannerTagline,
  introTitle,
  introSub,
  ctaLabel,
  ctaCaption,
  "blocks": blocks[]{ title, body, bullets, image }
`

const problemsSectionRedesignFields = /* groq */ `
  chipText,
  heading,
  subheading,
  fastListing   { ${problemsSectionRedesignFeatureFields} },
  smartPricing  { ${problemsSectionRedesignFeatureFields} },
  virtualModels { ${problemsSectionRedesignFeatureFields} }
`

const featureGridSectionFields = /* groq */ `
  ${sectionFields}
  eyebrow,
  "heading": coalesce(heading, ""),
  subheading,
  backgroundColor,
  backgroundColorCustom,
  cards[]{
    _key,
    "icon": coalesce(icon, "check"),
    "title": coalesce(title, ""),
    "description": coalesce(description, ""),
    image {
      ${imageFields}
    }
  }
`

const featureBentoSectionFields = /* groq */ `
  ${sectionFields}
  "heading": coalesce(heading, ""),
  "subheading": coalesce(subheading, ""),
  cards[]{
    _key,
    "kicker": coalesce(kicker, ""),
    "title": coalesce(title, ""),
    "description": coalesce(description, ""),
    "statValue": coalesce(statValue, ""),
    "statLabel": coalesce(statLabel, ""),
    ctaLabel,
    "href": coalesce(href, "#"),
    "visual": coalesce(visual, "listing"),
    "background": coalesce(background, "#7676BF"),
    "lightText": coalesce(lightText, false)
  }
`

const caseStudyBannerSectionFields = /* groq */ `
  ${sectionFields}
  companyLogo {
    ${imageFields}
  },
  "companyName": coalesce(companyName, ""),
  "headline": coalesce(headline, ""),
  "contextText": coalesce(contextText, ""),
  "statShopsValue": coalesce(statShopsValue, ""),
  "statShopsLabel": coalesce(statShopsLabel, ""),
  "statLocationValue": coalesce(statLocationValue, ""),
  "statLocationLabel": coalesce(statLocationLabel, ""),
  photo {
    ${imageFields}
  },
  "photoTreatment": coalesce(photoTreatment, "card")
`

const stripBannerSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, ""),
  "subtitle": coalesce(subtitle, ""),
  image {
    ${imageFields}
  },
  "headline": coalesce(headline, ""),
  "highlight": coalesce(highlight, ""),
  "buttonLabel": coalesce(buttonLabel, "Read more"),
  "href": select(
    linkType == "external" => externalUrl,
    internalLink->_type == "post" => "/" + coalesce(internalLink->locale, $locale) + "/blog/" + internalLink->slug.current,
    internalLink->_type == "page" => "/" + coalesce(internalLink->locale, $locale) + internalLink->slug.current
  ),
  "openInNewTab": coalesce(openInNewTab, false),
  "caption": coalesce(caption, "")
`

const testimonialCarouselSectionFields = /* groq */ `
  ${sectionFields}
  "title": coalesce(title, "What our customers say"),
  quotes[]{
    _key,
    "quote": coalesce(quote, ""),
    "name": coalesce(name, ""),
    "role": coalesce(role, ""),
    "portrait": portrait.asset->url,
    imagePosition,
    "logo": logo.asset->url
  }
`

// Hero Journey V2. Images resolve to plain URLs; list rows share one shape.
const heroJourneyV2RowFields = /* groq */ `
  "title": coalesce(title, ""),
  "subtitle": coalesce(subtitle, ""),
  "value": coalesce(value, ""),
  percent,
  "image": image.asset->url,
  logo
`

const heroJourneyV2StepFields = /* groq */ `
  "step": coalesce(step, ""),
  "label": coalesce(label, ""),
  "tip": coalesce(tip, ""),
  "screenTitle": coalesce(screenTitle, ""),
  "description": coalesce(description, ""),
  device,
  aiNote, askingLabel, explainLabel, compsLabel,
  channelsHeading, liveLabel,
  soldWhere, cardEyebrow, cardValue, cardDetail,
  draftLabel, labelDate, printLabel, attachedLabel,
  pinLabel, shopName, shopMeta, shopBadge, justInLabel, inStockLabel,
  dashboardTitle, periodLabel, accountName, accountScope, integrationsTitle,
  trendsTitle, bestSellersTitle, bestSellerDetail,
  "integrations": coalesce(integrations, []),
  "channels": coalesce(channels[]{ ${heroJourneyV2RowFields} }, []),
  "statusRows": coalesce(statusRows[]{ ${heroJourneyV2RowFields} }, []),
  "drafts": coalesce(drafts[]{ ${heroJourneyV2RowFields} }, []),
  "catalogue": coalesce(catalogue[]{ ${heroJourneyV2RowFields} }, []),
  "kpis": coalesce(kpis[]{ ${heroJourneyV2RowFields} }, []),
  "bestSellers": coalesce(bestSellers[]{ ${heroJourneyV2RowFields} }, [])
`

const heroJourneyV2ItemFields = /* groq */ `
  "key": _key,
  "thumbnailLabel": coalesce(thumbnailLabel, ""),
  "name": coalesce(name, ""),
  "shortName": coalesce(shortName, name, ""),
  "bestSellerTitle": coalesce(bestSellerTitle, name, ""),
  "noun": coalesce(noun, thumbnailLabel, "item"),
  "photo": photo.asset->url,
  "cutout": cutout.asset->url,
  "styledResult": styledResult.asset->url,
  "styledFrom": styledFrom.asset->url,
  "thumbnail": thumbnail.asset->url,
  "scanImage": scanImage.asset->url,
  "presentation": coalesce(presentation, "photo"),
  "stylingEffect": coalesce(stylingEffect, "wipe"),
  "isFashion": coalesce(isFashion, false),
  "detailFocus": coalesce(detailFocus, "50% 50%"),
  "scanFocus": coalesce(scanFocus, "50% 50%"),
  "listingTitle": coalesce(listingTitle, name, ""),
  "description": coalesce(description, ""),
  "specs": coalesce(specs[]{ "name": coalesce(name, ""), "value": coalesce(value, "") }, []),
  "searchQuery": coalesce(searchQuery, ""),
  "measureWidth": coalesce(measureWidth, ""),
  "measureHeight": coalesce(measureHeight, ""),
  "sizeChip": coalesce(sizeChip, ""),
  "measureRows": coalesce(measureRows[]{ "name": coalesce(name, ""), "value": coalesce(value, "") }, []),
  "beforeLabel": coalesce(beforeLabel, "Original"),
  "afterLabel": coalesce(afterLabel, "BG removal"),
  "stylingTitle": coalesce(stylingTitle, ""),
  "stylingSubtitle": coalesce(stylingSubtitle, ""),
  "price": coalesce(price, ""),
  "soldPrice": coalesce(soldPrice, price, ""),
  "median": coalesce(median, ""),
  "low": coalesce(low, ""),
  "high": coalesce(high, ""),
  "position": coalesce(position, 50),
  "marketComparison": coalesce(marketComparison, ""),
  "marketDirection": coalesce(marketDirection, "above"),
  "comparableCount": coalesce(comparableCount, "214"),
  "comps": coalesce(comps[]{ ${heroJourneyV2RowFields} }, []),
  soldWhere,
  "statusRows": statusRows[]{ ${heroJourneyV2RowFields} }
`

const heroJourneyV2SectionFields = /* groq */ `
  ${sectionFields}
  margins,
  "stepSeconds": coalesce(stepSeconds, 3),
  "resumeSeconds": coalesce(resumeSeconds, 5),
  "reduceMotion": coalesce(reduceMotion, false),
  "inStoreFirstIn": coalesce(inStoreFirstIn, []),
  "heading": coalesce(heading, ""),
  "body": coalesce(body, ""),
  "showBodyOnMobile": coalesce(showBodyOnMobile, false),
  "primaryButtonLabel": coalesce(primaryButtonLabel, ""),
  primaryButtonLink,
  "secondaryButtonLabel": coalesce(secondaryButtonLabel, ""),
  secondaryButtonLink,
  "logo": logo.asset->url,
  "journeys": coalesce(journeys[]{
    "journey": coalesce(journey, "online"),
    "buttonLabel": coalesce(buttonLabel, ""),
    "buttonLabelShort": coalesce(buttonLabelShort, buttonLabel, ""),
    "ctaEyebrow": coalesce(ctaEyebrow, ""),
    "ctaFeatures": coalesce(ctaFeatures[]{ "label": coalesce(label, ""), step }, []),
    "steps": coalesce(steps[]{ ${heroJourneyV2StepFields} }, []),
    "items": coalesce(items[]{ ${heroJourneyV2ItemFields} }, [])
  }, []),
  "signup": signup{
    "enabled": coalesce(enabled, true),
    hubspotFormId,
    interestProperty,
    "countdownSeconds": coalesce(countdownSeconds, 12),
    heading, subheading, nextJourneyLabel, keepWatchingLabel, changeFeatureLabel, formHeading,
    firstNamePlaceholder, lastNamePlaceholder, emailPlaceholder, organisationPlaceholder, submitLabel,
    missingFieldsError, invalidEmailError, submitError,
    successHeading, successBody, successBodyNoFeatures, calendarHeading
  },
  // The site-wide booking page, shown in the sign-up card once the form is sent.
  "meetingLink": *[_id == "settings"][0].hubspotMeetingLink{ url, enabled }
`

const pageBuilderSections = /* groq */ `
  _type == "heroSection" => {
    ${heroSectionFields}
  },
  _type == "heroJourneySection" => {
    ${heroJourneySectionFields}
  },
  _type == "heroJourneyV2Section" => {
    ${heroJourneyV2SectionFields}
  },
  _type == "heroVideoSection" => {
    ${heroVideoSectionFields}
  },
  _type == "heroContentSection" => {
    ${heroContentSectionFields}
  },
  _type == "contentSection" => {
    ${contentSectionFields}
  },
  _type == "supportedMarketplacesSection" => {
    ${supportedMarketplacesSectionFields}
  },
  _type == "trustedBySection" => {
    ${trustedBySectionFields}
  },
  _type == "featuresSection" => {
    ${featuresSectionFields}
  },
  _type == "featureCardsSection" => {
    ${featureCardsSectionFields}
  },
  _type == "aboutSection" => {
    ${aboutSectionFields}
  },
  _type == "comparePricingTableSection" => {
    ${comparePricingTableSectionFields}
  },
  _type == "planPricingSection" => {
    ${planPricingSectionFields}
  },
  _type == "calculatorSection" => {
    ${calculatorSectionFields}
  },
  _type == "ctaSection" => {
    ${ctaSectionFields}
  },
  _type == "ctaBandSection" => {
    ${ctaBandSectionFields}
  },
  _type == "phoneDemoVideoSection" => {
    ${phoneDemoVideoSectionFields}
  },
  _type == "ctaButtonSection" => {
    ${ctaButtonSectionFields}
  },
  _type == "customerQuoteSection" => {
    ${customerQuoteSectionFields}
  },
  _type == "faqSection" => {
    ${faqSectionFields}
  },
  _type == "itemTypesSection" => {
    ${itemTypesSectionFields}
  },
  _type == "statsSection" => {
    ${statsSectionFields}
  },
  _type == "gallerySection" => {
    ${gallerySectionFields}
  },
  _type == "twoColumnSection" => {
    ${twoColumnSectionFields}
  },
  _type == "downloadGateSection" => {
    ${downloadGateSectionFields}
  },
  _type == "mapSection" => {
    ${mapSectionFields}
  },
  _type == "teamSection" => {
    ${teamSectionFields}
  },
  _type == "pressSection" => {
    ${pressSectionFields}
  },
  _type == "textWithIllustrationSection" => {
    ${textWithIllustrationSectionFields}
  },
  _type == "pageHeaderSection" => {
    ${pageHeaderSectionFields}
  },
  _type == "spacerSection" => {
    ${spacerSectionFields}
  },
  _type == "interactiveHeroSection" => {
    ${interactiveHeroSectionFields}
  },
  _type == "keyBenefitsSection" => {
    ${keyBenefitsSectionFields}
  },
  _type == "videoEmbedSection" => {
    ${videoEmbedSectionFields}
  },
  _type == "imageBlockSection" => {
    ${imageBlockSectionFields}
  },
  _type == "contentSliderSection" => {
    ${contentSliderSectionFields}
  },
  _type == "whyChooseSection" => {
    ${whyChooseSectionFields}
  },
  _type == "marketplaceLinksSection" => {
    ${sectionFields}
    ...
  },
  _type == "problemsSectionRedesign" => {
    ${sectionFields}
    ${problemsSectionRedesignFields}
  },
  _type == "featureBentoSection" => {
    ${featureBentoSectionFields}
  },
  _type == "featureGridSection" => {
    ${featureGridSectionFields}
  },
  _type == "caseStudyBannerSection" => {
    ${caseStudyBannerSectionFields}
  },
  _type == "stripBannerSection" => {
    ${stripBannerSectionFields}
  },
  _type == "contentBlocksSection" => {
    ${contentBlocksSectionFields}
  },
  _type == "testimonialCarouselSection" => {
    ${testimonialCarouselSectionFields}
  }
`

// Main page query using all section fragments
export const b2bPageQuery = defineQuery(/* groq */ `
  *[
    _type == "page"
    && domain == "b2b"
    && locale == $locale
    && slug.current == $slug
  ][0] {
    ...,
    pageBuilder[]{
      ${sectionFields}
      ${pageBuilderSections}
    },
    seo {
      ...
    }
  }
`)

const navFields = /* groq */ `
  _type,
  _id,
  locale,
  "slug": slug.current,
`

/**
 * For `internalLink` in navigation items.
 */
const hierarchicalNavFields = /* groq */ `
  _type,
  _id,
  _ref,
  locale,
  "slug": slug.current,
  parent->{
    _type,
    _id,
    _ref,
    locale,
    "slug": slug.current,
  }
`

export const pageNavQuery = defineQuery(/* groq */ `
  *[_type == "page" && _id == $id][0] {
    ${hierarchicalNavFields}
  }
`)

export const categoryNavQuery = defineQuery(/* groq */ `
  *[_type == "category" && _id == $id][0] {
    ${hierarchicalNavFields}
  }
`)

export const postNavQuery = defineQuery(/* groq */ `
  *[_type == "post" && _id == $id][0] {
    ${navFields}
  }
`)

export const linkFields = /* groq */ `
  _key,
  "label": coalesce(
    label[_key == $locale][0].value,
    label[_key == "en"][0].value
  ),
  type,
  internalLink->{
    _type == "category" => {
      ${hierarchicalNavFields}
    },
    _type == "page" => {
      ${hierarchicalNavFields}
    },
    _type == "post" => {
      ${navFields}
    }
  },
  externalUrl,
  eventName,
  "isVisible": coalesce(
    isVisible[_key == $locale][0].value,
    isVisible[_key == "en"][0].value,
    true
  )
`

const contactFormFields = /* groq */ `
  "title": coalesce(title[_key == $locale][0].value, title[_key == "en"][0].value),
  "intro": coalesce(intro[_key == $locale][0].value, intro[_key == "en"][0].value),
  "submitLabel": coalesce(submitLabel[_key == $locale][0].value, submitLabel[_key == "en"][0].value),
  "sendingLabel": coalesce(sendingLabel[_key == $locale][0].value, sendingLabel[_key == "en"][0].value),
  "toastTitle": coalesce(toastTitle[_key == $locale][0].value, toastTitle[_key == "en"][0].value),
  "toastDescription": coalesce(toastDescription[_key == $locale][0].value, toastDescription[_key == "en"][0].value),
  fields[] {
    fieldType,
    "label": coalesce(label[_key == $locale][0].value, label[_key == "en"][0].value),
    required
  }
`

export const settingsQuery = defineQuery(`*[_type == "settings"][0] {
  ...,
  salesBanner {
    ...,
    link {
      ${linkFields}
    },
    countryVersions[] {
      _key,
      countries,
      text,
      color,
      link {
        ${linkFields}
      }
    }
  },
  timeBasedPopup {
    enabled,
    delaySeconds,
    "title": coalesce(title[_key == $locale][0].value, title[_key == "en"][0].value),
    "description": coalesce(description[_key == $locale][0].value, description[_key == "en"][0].value),
    image {
      ${imageFields}
    },
    ctaType,
    "ctaLabel": coalesce(ctaLabel[_key == $locale][0].value, ctaLabel[_key == "en"][0].value),
    link {
      ${linkFields}
    },
    expiryDate
  },
  hubspotMeetingLink {
    url,
    label,
    enabled
  },
  tallyFormEmbedUrl,
  contactForm {
    ${contactFormFields}
  }
}`)


const layoutNavigationFields = /* groq */ `
  navigation[]{
    ${linkFields}
  },
`

const internalLinkFields = /* groq */ `
  _type == "category" => {
    ${hierarchicalNavFields}
  },
  _type == "page" => {
    ${hierarchicalNavFields}
  },
  _type == "post" => {
    ${navFields}
  },
`

const layoutFooterLinkListFields = /* groq */ `
  links[]{
    _key,
    "label": coalesce(
      label[_key == $locale][0].value,
      label[_key == "en"][0].value
    ),
    type,
    internalLink->{
      ${internalLinkFields}
    },
    externalUrl,
    eventName,
    "isVisible": coalesce(
      isVisible[_key == $locale][0].value,
      isVisible[_key == "en"][0].value,
      true
    ),
  },
`

const layoutFooterRichTextFields = /* groq */ `
  ...,
  markDefs[] {
    ...,
    _type == "internalLink" => @->{
      ${internalLinkFields}
    }
  }
`

// TODO: Add rich text fields and figure out how to properly query them
// _type == "richText" =>{
//   ${layoutFooterRichTextFields}
// },

const layoutFooterFields = /* groq */ `
  footer {
    companyDescription[] {
      ${layoutFooterRichTextFields}
    },
    columns[] {
      _key,
      "title": coalesce(
        title[_key == $locale][0].value,
        title[_key == "en"][0].value
      ),
      colSpan,
      content[] {
        _type,
        _key,
        _type == "linkList" => {
          ${layoutFooterLinkListFields}
        },
        _type == "image" => {
          ${imageFields}
        },
        _type == "richText" => {
          ${layoutFooterRichTextFields}
        },
      }
    },
    "copyright": coalesce(
      copyright[_key == $locale][0].value,
      copyright[_key == "en"][0].value,
      "Copyright © 2024 Minimist"
    )
  }
`

// At the top of the file, add the shared theme fields constant
const themeLayoutFields = /* groq */ `
  layout {
    name,
    showSectionNav,
    sectionNavGap,
    startContentAtTop,
    spaceBelowNav,
    ${layoutNavigationFields}
    showFooter,
    ${layoutFooterFields}
  },
`


// Update the theme queries to use the shared fields
export const themeQuery = defineQuery(`
  *[_type == "theme" && _id == $themeId][0] {
    _id,
    ${themeLayoutFields}
  }
`)

export const defaultThemeQuery = defineQuery(`
  *[_type == "settings"][0] {
    defaultTheme-> {
      ${themeLayoutFields}
    },
    stagingTheme-> {
      ${themeLayoutFields}
    }
  }
`)

// Query to get pages grouped by navigation groups
export const navigationGroupsQuery = defineQuery(`
  {
    "groups": *[
      _type == "navigationGroup" &&
      isActive == true
    ] | order(order asc, name asc) {
      _id,
      name,
      "slug": slug.current,
      order
    },
    "pages": *[
      _type == "page" &&
      defined(navigationGroup) &&
      defined(navigationGroup._ref) &&
      (locale == $locale || !defined(locale)) &&
      defined(slug.current)
    ] {
      _id,
      title,
      "slug": slug.current,
      navigationOrder,
      navigationSubtitle,
      navigationIcon { ${imageFields} },
      "groupRef": navigationGroup._ref
    },
    "caseStudies": *[
      _type == "page" &&
      string::startsWith(slug.current, "/case-study-") &&
      (locale == $locale || !defined(locale)) &&
      defined(slug.current)
    ] | order(navigationOrder asc, title asc) {
      _id,
      title,
      "slug": slug.current,
      navigationOrder,
      navigationSubtitle,
      navigationIcon { ${imageFields} }
    }
  }
`)

/** Result shape of {@link changelogFeedQuery}. */
export interface ChangelogFeedEntry {
  _id: string
  title: string
  slug: string | null
  tag: string | null
  /** Surfaces the entry targets (see CHANGELOG_PLATFORMS); null ⇒ XL-only (pre-field entries). */
  platforms: string[] | null
  publishedAt: string
  summary: string | null
  /** Full PortableText body — lets in-app consumers (XL) render a detail view. */
  body: PortableTextBlock[] | null
  image: { url: string | null; width: number | null; height: number | null; alt: string | null } | null
  cta: { label: string | null; href: string | null; tourId: string | null } | null
  /** Second CTA used inside the mobile app (href is an in-app route; tourId an app tutorial). */
  appCta: { label: string | null; href: string | null; tourId: string | null } | null
}

/** Result of {@link changelogEntryForSlackQuery}. */
export interface ChangelogSlackEntry {
  _id: string
  title: string | null
  slug: string | null
  tag: string | null
  /** null ⇒ XL-only (entries predating the field), as in the feed. */
  platforms: string[] | null
  summary: string | null
  imageUrl: string | null
  imageAlt: string | null
  linearIssueId: string | null
}

/**
 * Projection for the machine feed (/api/changelog): flattens the PortableText
 * body to plain text server-side via pt::text for `summary`, and also ships the
 * raw `body` blocks so in-app consumers (XL, the mobile app) can render a detail
 * view. Since MIN-3460 this is the only reader of changelog entries — the public
 * page and its RSS feed, and the list/detail/slug queries that fed them, are
 * gone.
 *
 * $platform filters to entries targeting one surface ("app" / "xl"); pass null
 * for the unfiltered feed. Filtering happens in GROQ (not post-fetch) so $end
 * counts post-filter entries. Entries without the field count as XL-only.
 */
export const changelogFeedQuery = defineQuery(/* groq */ `
  *[_type == "changelogEntry" && defined(publishedAt)
    && ($platform == null || $platform in coalesce(platforms, ["xl"]))
  ] | order(publishedAt desc) [0...$end] {
    _id,
    title,
    "slug": slug.current,
    tag,
    platforms,
    publishedAt,
    "summary": pt::text(body),
    body,
    "image": coverImage {
      "url": asset->url,
      "width": asset->metadata.dimensions.width,
      "height": asset->metadata.dimensions.height,
      alt
    },
    cta {
      label,
      href,
      tourId
    },
    appCta {
      label,
      href,
      tourId
    }
  }
`)

/**
 * One changelog entry by id, projected for the #whats-new Slack announcer
 * (/api/changelog/slack-webhook). The Sanity webhook hands that route only the
 * `_id`; it re-reads the document here rather than trusting the payload.
 */
export const changelogEntryForSlackQuery = defineQuery(/* groq */ `
  *[_type == "changelogEntry" && _id == $id][0] {
    _id,
    title,
    "slug": slug.current,
    tag,
    platforms,
    "summary": pt::text(body),
    "imageUrl": coverImage.asset->url,
    "imageAlt": coverImage.alt,
    linearIssueId
  }
`)

// --- Help Center feed --------------------------------------------------------
// Feeds the in-app Help Center in Minimist XL (behind the login), served from
// /api/help — same machine-feed pattern as the changelog above. Content is
// authored in the Studio (migrated one-time from Intercom). Inline image assets
// and cross-article link references are resolved server-side because XL has no
// Sanity image-URL builder and shouldn't need the Studio schema to build links.

/** Result shape of {@link helpCollectionsFeedQuery}. */
export interface HelpCollectionFeedEntry {
  _id: string
  title: string
  slug: string | null
  description: string | null
  order: number | null
}

/** Published Help collections for one locale, ordered for the Help Center nav. */
export const helpCollectionsFeedQuery = defineQuery(/* groq */ `
  *[_type == "helpCollection" && locale == $locale && defined(slug.current)]
  | order(coalesce(order, 100) asc, title asc) {
    _id,
    title,
    "slug": slug.current,
    description,
    order
  }
`)

/** Result shape of {@link helpArticlesFeedQuery}. */
export interface HelpArticleFeedEntry {
  _id: string
  title: string
  slug: string | null
  excerpt: string | null
  order: number | null
  updatedAt: string | null
  collectionId: string | null
  collectionSlug: string | null
  collectionTitle: string | null
  collectionOrder: number | null
  summary: string | null
  body: PortableTextBlock[] | null
}

/**
 * Published Help articles for one locale. The body is projected so consumers get
 * everything they need without a Sanity client of their own:
 *   - inline `image` blocks carry a resolved CDN `url` (+ dimensions),
 *   - `helpVideo` blocks carry a resolved `posterUrl` (the video `url` itself is
 *     passed through untouched — the consumer derives the embed src from it),
 *   - `helpArticleLink` annotations carry the target's `articleSlug` /
 *     `collectionSlug` so XL can build an in-app /help link.
 */
export const helpArticlesFeedQuery = defineQuery(/* groq */ `
  *[_type == "helpArticle" && locale == $locale && defined(slug.current)]
  | order(coalesce(order, 100) asc, title asc) {
    _id,
    title,
    "slug": slug.current,
    excerpt,
    order,
    updatedAt,
    "collectionId": collection->_id,
    "collectionSlug": collection->slug.current,
    "collectionTitle": collection->title,
    "collectionOrder": collection->order,
    "summary": pt::text(body),
    body[]{
      ...,
      _type == "image" => {
        "url": asset->url,
        "width": asset->metadata.dimensions.width,
        "height": asset->metadata.dimensions.height
      },
      _type == "helpVideo" => {
        "posterUrl": posterImage.asset->url
      },
      markDefs[]{
        ...,
        _type == "helpArticleLink" => {
          "articleSlug": reference->slug.current,
          "collectionSlug": reference->collection->slug.current
        }
      }
    }
  }
`)

// Charity Retail page (singleton) — editable surfaces only; the rest is hardcoded.
export const charityRetailPageQuery = defineQuery(`*[_type == "charityRetailPage"][0]{
  heroBadge,
  heroHeadline,
  heroSubheadline,
  heroPrimaryCtaLabel,
  heroSecondaryCtaLabel,
  heroTicks,
  "heroImages": heroImages[]{ "url": asset->url, alt },
  marqueeHeading,
  "marqueeLogos": marqueeLogoSet->partners[]{ "name": coalesce(name, ""), "src": logoOrVideo.image.asset->url },
  twoPath{
    eyebrow,
    heading,
    lede,
    paths[]{ icon, kicker, title, body, checklist, ctaLabel, ctaHref, statValue, statLabel }
  },
  testimonials[]{ quote, name, title, company, "portrait": portrait.asset->url, "logo": logo.asset->url },
  caseStudiesHeading,
  caseStudies[]{ charityName, "image": image.asset->url, link },
  ctaHeadline,
  ctaSubcopy,
  ctaButtonLabel
}`)

export const aboutPageQuery = defineQuery(`*[_type == "aboutPage"][0]{
  storyHeadline,
  storyBody,
  whyHeading,
  features[]{ iconName, title, body },
  visualSlides[]{ "image": image.asset->url, "alt": image.alt, title, body },
  investmentHeadline,
  investmentHighlight,
  investmentCtaLabel,
  investmentCtaUrl,
  investmentReadTime,
  "investmentPhoto": investmentPhoto.asset->url,
  teamHeading,
  teamBody,
  teamBullets,
  "teamPhoto": teamPhoto.asset->url
}`)

// One of the four standalone feature pages, selected by its routeKey.
// Images resolve to plain URLs (empty ⇒ null ⇒ component falls back to /public).
// Cross-links dereference the linked featurePage for its card summary.
export const featurePageQuery = defineQuery(`*[_type == "featurePage" && routeKey == $routeKey][0]{
  routeKey,
  name,
  category,
  theme,
  bannerHeadline,
  bannerTagline,
  heroVisual,
  heroCta{ label, href },
  heroCtaCaption,
  heroMedia{
    mediaType,
    image{ "url": asset->url, alt },
    video{ url, alt, autoplay, loop, muted, "poster": poster.asset->url }
  },
  heroSlideshow{
    autoplay,
    intervalSeconds,
    "images": images[]{ "url": asset->url, alt }
  },
  card{ eyebrow, problem, body, ctaLabel, metric{ value, label } },
  introTitle,
  introSub,
  blocks[]{ title, body, bullets, visual, imageFit, "image": image.asset->url, "images": images[]{ "url": asset->url, "alt": alt } },
  exploreReports{
    heading,
    subheading,
    autoplay,
    intervalSeconds,
    "images": images[]{ "url": asset->url, alt }
  },
  ourFixItems[]{ eyebrow, title, body, icon },
  testimonials[]{ quote, name, location, "photo": photo.asset->url },
  demoVideo{ videoId, eyebrow, heading, subcopy, "poster": poster.asset->url },
  caseStudyCard{ headline, highlight, ctaLabel, ctaLink, caption, "image": image.asset->url, "imageAlt": image.alt },
  closingCta{ heading, subcopy, button{ label, href } },
  "crossLinks": crossLinks[]->{ routeKey, name, theme, "problem": card.problem },
  seo{ title, description, keywords, noIndex }
}`)

// ---------------------------------------------------------------------------
// Help center → agent RAG index (RFC-002 §8)
//
// The agent retrieves over the SAME `helpArticle` documents that back the in-app
// Help Center (MIN-2785, migrated from Intercom) — one collection, one source of
// truth, so an answer the agent cites is the article the reader can open. The
// listing/detail projections live above (`helpArticlesFeedQuery`); this is the
// ingestion projection.
// ---------------------------------------------------------------------------

/**
 * The shape the reindex pipeline consumes: everything mini-web needs to chunk +
 * embed an article into `knowledge_chunks`. `_rev` makes the job idempotent
 * (skip if the (_id, _rev) pair is already indexed). No locale filter — reindex
 * covers every locale; the webhook passes a single `$id` to reindex one doc, or
 * a null `$id` to backfill all.
 *
 * Both `body` and `plainText` are projected on purpose: the chunker splits on
 * heading blocks in `body` (so a chunk can carry its `heading` for citation),
 * and falls back to `plainText` for articles with no headings.
 *
 * DRAFTS ARE EXCLUDED IN THE QUERY ITSELF, not only via the client perspective.
 * mnm-st's own reads are safe because `fetchPublished` pins
 * `perspective: "published"`, but mini-web is a separate Sanity client and the
 * API's default `raw` perspective returns `drafts.*` documents. Indexing a draft
 * would let the agent quote and link an article no reader can open — and a
 * half-written one at that. The `path("drafts.**")` guard makes this query
 * correct no matter how the caller configures its client; a caller that ALSO
 * sets the published perspective just makes it redundant, which is the point.
 *
 * Unpublish/delete is the other half and belongs to the reindexer, not here: a
 * query can only report what exists, so the job must drop `knowledge_chunks`
 * rows for documents this query no longer returns, or the agent keeps citing
 * retired articles.
 *
 * Result type: `HelpArticlesForReindexQueryResult` in `src/sanity/types.ts`
 * (generated by `pnpm cms:typegen`) — no hand-written mirror, so it can't drift.
 */
export const helpArticlesForReindexQuery = defineQuery(/* groq */ `
  *[
    _type == "helpArticle" &&
    !(_id in path("drafts.**")) &&
    defined(slug.current) &&
    (!defined($id) || _id == $id)
  ] {
    _id,
    _rev,
    "locale": coalesce(locale, "en"),
    title,
    "slug": slug.current,
    excerpt,
    updatedAt,
    intercomId,
    "collectionSlug": collection->slug.current,
    "collectionTitle": collection->title,
    body,
    "plainText": pt::text(body)
  }
`)
// scratch
